/* ============================================
   ALnoor Theme — Product Form JavaScript
   Variant selection, quantity, AJAX add-to-cart,
   sticky bar, accordions, size guide modal.
   ============================================ */

(function () {
  'use strict';

  /* -----------------------------------------------
     ProductForm — Custom element for product forms
     ----------------------------------------------- */
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.section = this.closest('[data-section-id]');
      this.productJSON = null;
      this.currentVariant = null;
      this.optionInputs = [];
      this.init();
    }

    init() {
      this.parseProductData();
      this.cacheDOM();
      this.bindEvents();
      this.initStickyATC();
      this.initSizeGuide();
    }

    parseProductData() {
      const jsonEl = this.section
        ? this.section.querySelector('[data-product-json]')
        : document.querySelector('[data-product-json]');
      if (jsonEl) {
        try {
          this.productJSON = JSON.parse(jsonEl.textContent);
        } catch (e) {
          console.error('ProductForm: Failed to parse product JSON', e);
        }
      }
      if (this.productJSON) {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        if (variantId) {
          this.currentVariant = this.productJSON.variants.find(
            (v) => v.id === parseInt(variantId, 10)
          );
        }
        if (!this.currentVariant) {
          this.currentVariant =
            this.productJSON.variants.find((v) => v.available) ||
            this.productJSON.variants[0];
        }
      }
    }

    cacheDOM() {
      /* Option inputs (radio buttons for color/size) */
      this.optionInputs = Array.from(
        this.querySelectorAll('[data-option-input]')
      );

      /* Hidden variant select */
      this.variantSelect = this.querySelector('[data-variant-select]');

      /* Price elements */
      this.priceEl = this.section
        ? this.section.querySelector('[data-price]')
        : null;
      this.comparePriceEl = this.section
        ? this.section.querySelector('[data-compare-price]')
        : null;
      this.priceBadgeEl = this.section
        ? this.section.querySelector('[data-price-badge]')
        : null;
      this.priceWrap = this.section
        ? this.section.querySelector('[data-product-price]')
        : null;

      /* Add to cart */
      this.addToCartBtn = this.querySelector('[data-add-to-cart]');
      this.addToCartText = this.querySelector('[data-add-to-cart-text]');
      this.addLoadingEl = this.querySelector('[data-add-loading]');

      /* Quantity */
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.quantityMinus = this.querySelector('[data-quantity-minus]');
      this.quantityPlus = this.querySelector('[data-quantity-plus]');

      /* Stock counter */
      this.stockCounterWrap = this.section
        ? this.section.querySelector('[data-stock-counter]')
        : null;

      /* Option value display labels */
      this.optionValueDisplays = Array.from(
        this.querySelectorAll('[data-option-value-display]')
      );

      /* Sticky ATC */
      this.stickyATC = this.section
        ? this.section.querySelector('[data-sticky-atc]')
        : null;
      this.stickyPrice = this.stickyATC
        ? this.stickyATC.querySelector('[data-sticky-price]')
        : null;
      this.stickyVariantSelect = this.stickyATC
        ? this.stickyATC.querySelector('[data-sticky-variant-select]')
        : null;
      this.stickyAddBtn = this.stickyATC
        ? this.stickyATC.querySelector('[data-sticky-add-to-cart]')
        : null;
      this.stickyATCText = this.stickyATC
        ? this.stickyATC.querySelector('[data-sticky-atc-text]')
        : null;
    }

    bindEvents() {
      /* Variant option changes */
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', this.onOptionChange.bind(this));
      });

      /* Form submit (add to cart) */
      if (this.form) {
        this.form.addEventListener('submit', this.onFormSubmit.bind(this));
      }

      /* Quantity buttons */
      if (this.quantityMinus) {
        this.quantityMinus.addEventListener('click', () =>
          this.adjustQuantity(-1)
        );
      }
      if (this.quantityPlus) {
        this.quantityPlus.addEventListener('click', () =>
          this.adjustQuantity(1)
        );
      }

      /* Sticky ATC variant select sync */
      if (this.stickyVariantSelect) {
        this.stickyVariantSelect.addEventListener('change', (e) => {
          this.onStickyVariantChange(e);
        });
      }

      /* Sticky ATC button */
      if (this.stickyAddBtn) {
        this.stickyAddBtn.addEventListener('click', () => {
          this.onStickyAddToCart();
        });
      }

      /* Copy URL share button */
      const copyBtn = this.section
        ? this.section.querySelector('[data-copy-url]')
        : null;
      if (copyBtn) {
        copyBtn.addEventListener('click', () => {
          const url = copyBtn.getAttribute('data-copy-url');
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(() => {
              copyBtn.setAttribute('aria-label', 'Link copied!');
              setTimeout(() => {
                copyBtn.setAttribute(
                  'aria-label',
                  'Copy link'
                );
              }, 2000);
            });
          }
        });
      }
    }

    /* --- Variant Selection --- */
    onOptionChange() {
      const selectedOptions = this.getSelectedOptions();
      this.updateOptionDisplayLabels(selectedOptions);
      const variant = this.findVariant(selectedOptions);

      if (variant) {
        this.currentVariant = variant;
        this.updateVariantSelect(variant);
        this.updatePrice(variant);
        this.updateAddToCartButton(variant);
        this.updateURL(variant);
        this.updateStockCounter(variant);
        this.updateStickyATC(variant);
        this.updateGalleryImage(variant);

        /* Publish variant change event */
        if (typeof PubSub !== 'undefined') {
          PubSub.publish('variant:change', {
            variant: variant,
            product: this.productJSON,
          });
        }
      } else {
        /* No matching variant — mark as unavailable */
        this.updateAddToCartButton(null);
        if (this.stickyAddBtn) {
          this.stickyAddBtn.disabled = true;
          if (this.stickyATCText) this.stickyATCText.textContent = 'Unavailable';
        }
      }
    }

    getSelectedOptions() {
      const options = [];
      const fieldsets = this.querySelectorAll('[data-option-index]');
      fieldsets.forEach((fieldset) => {
        const checked = fieldset.querySelector('[data-option-input]:checked');
        if (checked) {
          options.push(checked.value);
        }
      });
      return options;
    }

    updateOptionDisplayLabels(options) {
      this.optionValueDisplays.forEach((el, index) => {
        if (options[index]) {
          el.textContent = options[index];
        }
      });
    }

    findVariant(selectedOptions) {
      if (!this.productJSON || !this.productJSON.variants) return null;
      return this.productJSON.variants.find((variant) => {
        return selectedOptions.every((val, index) => {
          return variant.options[index] === val;
        });
      });
    }

    updateVariantSelect(variant) {
      if (this.variantSelect) {
        this.variantSelect.value = variant.id;
      }
    }

    updatePrice(variant) {
      if (!this.priceEl) return;

      this.priceEl.textContent = formatMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (this.comparePriceEl) {
          this.comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        }
        if (this.priceBadgeEl) {
          const savings = Math.round(
            ((variant.compare_at_price - variant.price) /
              variant.compare_at_price) *
              100
          );
          this.priceBadgeEl.textContent = `-${savings}%`;
          this.priceBadgeEl.style.display = '';
        }
        if (this.priceWrap) {
          this.priceWrap.classList.add('product-page__price--on-sale');
        }
      } else {
        if (this.comparePriceEl) this.comparePriceEl.style.display = 'none';
        if (this.priceBadgeEl) this.priceBadgeEl.style.display = 'none';
        if (this.priceWrap) {
          this.priceWrap.classList.remove('product-page__price--on-sale');
        }
      }
    }

    updateAddToCartButton(variant) {
      if (!this.addToCartBtn || !this.addToCartText) return;

      if (!variant) {
        this.addToCartBtn.disabled = true;
        this.addToCartText.textContent = 'Unavailable';
        return;
      }

      if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartText.textContent = 'Add to Cart';
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartText.textContent = 'Sold Out';
      }
    }

    updateURL(variant) {
      if (!variant || !window.history) return;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateStockCounter(variant) {
      /* Re-render stock counter by fetching section HTML or simple DOM swap */
      if (!this.stockCounterWrap || !this.section) return;
      const sectionId = this.section.getAttribute('data-section-id');
      const productUrl = this.section.getAttribute('data-product-url');
      if (!productUrl || !sectionId) return;

      fetch(
        `${productUrl}?variant=${variant.id}&section_id=${sectionId}`
      )
        .then((res) => res.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newStockCounter = doc.querySelector('[data-stock-counter]');
          if (newStockCounter) {
            this.stockCounterWrap.innerHTML = newStockCounter.innerHTML;
          }
        })
        .catch(() => {});
    }

    updateStickyATC(variant) {
      if (this.stickyPrice) {
        this.stickyPrice.textContent = formatMoney(variant.price);
      }
      if (this.stickyVariantSelect) {
        this.stickyVariantSelect.value = variant.id;
      }
      if (this.stickyAddBtn && this.stickyATCText) {
        if (variant.available) {
          this.stickyAddBtn.disabled = false;
          this.stickyATCText.textContent = 'Add to Cart';
        } else {
          this.stickyAddBtn.disabled = true;
          this.stickyATCText.textContent = 'Sold Out';
        }
      }
    }

    /* --- Update Gallery Image on Variant Change --- */
updateGalleryImage(variant) {
  if (!variant || !variant.featured_image || !this.section) return;

  const gallery = this.section.querySelector('[data-media-gallery]');
  if (!gallery || !gallery._mediaGallery) return;

  gallery._mediaGallery.goToImageBySrc(variant.featured_image.src);
}

    /* --- Sticky variant select change --- */
    onStickyVariantChange(e) {
      const variantId = parseInt(e.target.value, 10);
      const variant = this.productJSON.variants.find(
        (v) => v.id === variantId
      );
      if (!variant) return;

      this.currentVariant = variant;

      /* Sync the main form option inputs */
      variant.options.forEach((optionValue, index) => {
        const fieldset = this.querySelector(
          `[data-option-index="${index}"]`
        );
        if (fieldset) {
          const input = fieldset.querySelector(
            `[data-option-input][value="${CSS.escape(optionValue)}"]`
          );
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    /* --- Quantity --- */
    adjustQuantity(delta) {
      if (!this.quantityInput) return;
      const current = parseInt(this.quantityInput.value, 10) || 1;
      const newVal = Math.max(1, current + delta);
      this.quantityInput.value = newVal;
    }

    /* --- Add to Cart (AJAX) --- */
async onFormSubmit(e) {
  e.preventDefault();

  if (!this.currentVariant || !this.currentVariant.available) return;

  const quantity = parseInt(
    this.quantityInput ? this.quantityInput.value : 1,
    10
  );

  this.setLoadingState(true);

  try {
    const result = await CartAPI.add([
      {
        id: this.currentVariant.id,
        quantity: quantity,
      },
    ]);

    /* Publish cart:added event — cart.js will refresh drawer and open it */
    if (typeof PubSub !== 'undefined') {
      PubSub.publish('cart:added', result);
    }

    /* Brief success feedback */
    if (this.addToCartText) {
      this.addToCartText.textContent = 'Added!';
      setTimeout(() => {
        this.addToCartText.textContent = 'Add to Cart';
      }, 2000);
    }
  } catch (err) {
    console.error('Add to cart failed:', err);
    if (this.addToCartText) {
      this.addToCartText.textContent = 'Stock limit reached';
      setTimeout(() => {
        this.addToCartText.textContent = 'Add to Cart';
      }, 3000);
    }
  } finally {
    this.setLoadingState(false);
  }
}

    /* --- Sticky Add to Cart (AJAX) --- */
async onStickyAddToCart() {
  if (!this.currentVariant || !this.currentVariant.available) return;

  const quantity = parseInt(
    this.quantityInput ? this.quantityInput.value : 1,
    10
  );

  if (this.stickyAddBtn) this.stickyAddBtn.disabled = true;

  try {
    const result = await CartAPI.add([
      {
        id: this.currentVariant.id,
        quantity: quantity,
      },
    ]);

    /* Notify other components that the cart has changed */
    if (typeof PubSub !== 'undefined') {
      PubSub.publish('cart:added', result);
    }

    /* Brief success feedback */
    if (this.stickyATCText) {
      this.stickyATCText.textContent = 'Added!';
      setTimeout(() => {
        this.stickyATCText.textContent = 'Add to Cart';
      }, 2000);
    }
  } catch (err) {
    console.error('Sticky add to cart failed:', err);
  } finally {
    if (this.stickyAddBtn && this.currentVariant.available) {
      this.stickyAddBtn.disabled = false;
    }
  }
}

    setLoadingState(loading) {
      if (!this.addToCartBtn) return;
      if (loading) {
        this.addToCartBtn.classList.add('product-page__add-btn--loading');
        this.addToCartBtn.disabled = true;
        if (this.addLoadingEl) {
          this.addLoadingEl.classList.remove('visually-hidden');
          this.addLoadingEl.setAttribute('aria-hidden', 'false');
        }
      } else {
        this.addToCartBtn.classList.remove('product-page__add-btn--loading');
        if (this.currentVariant && this.currentVariant.available) {
          this.addToCartBtn.disabled = false;
        }
        if (this.addLoadingEl) {
          this.addLoadingEl.classList.add('visually-hidden');
          this.addLoadingEl.setAttribute('aria-hidden', 'true');
        }
      }
    }

    /* --- Sticky Add-to-Cart Bar --- */
    initStickyATC() {
      if (!this.stickyATC || !this.addToCartBtn) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.stickyATC.classList.remove('sticky-atc--visible');
              this.stickyATC.setAttribute('aria-hidden', 'true');
            } else {
              this.stickyATC.classList.add('sticky-atc--visible');
              this.stickyATC.setAttribute('aria-hidden', 'false');
            }
          });
        },
        {
          threshold: 0,
          rootMargin: '0px',
        }
      );

      observer.observe(this.addToCartBtn);
    }

    /* --- Size Guide Modal --- */
    initSizeGuide() {
      const modal = document.getElementById('size-guide-modal');
      if (!modal) return;

      const triggers = this.section
        ? this.section.querySelectorAll('[data-size-guide-trigger]')
        : document.querySelectorAll('[data-size-guide-trigger]');
      const closeBtns = modal.querySelectorAll('[data-size-guide-close]');

      let releaseFocus = null;

      const openModal = () => {
        modal.classList.add('size-guide-modal--open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';

        /* Trap focus */
        const closeBtn = modal.querySelector('.size-guide-modal__close');
        if (typeof trapFocus === 'function') {
          releaseFocus = trapFocus(modal, closeBtn);
        } else if (closeBtn) {
          closeBtn.focus();
        }
      };

      const closeModal = () => {
        modal.classList.remove('size-guide-modal--open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';

        if (releaseFocus) {
          releaseFocus();
          releaseFocus = null;
        }

        /* Return focus to last trigger */
        if (triggers.length > 0) {
          triggers[0].focus();
        }
      };

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', openModal);
      });

      closeBtns.forEach((btn) => {
        btn.addEventListener('click', closeModal);
      });

      /* Close on Escape */
      modal.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeModal();
        }
      });
    }
  }

  /* Register custom element */
  if (!customElements.get('product-form')) {
    customElements.define('product-form', ProductForm);
  }
})();
